import { type Connection, Position, type XYPosition } from '@xyflow/react';

import { TILE_DEFAULT_WIDTH } from '@/components/diagrams/components/Tile/constants';
import { DiagramSide } from '@/types/schema';

import {
  DiagramEditorTool,
  type DiagramQuickAddTool,
} from '../components/DiagramEditor/diagramEditorTools';
import { type DiagramConnectionEnds } from './diagramEdits';

/** The connection handle of each node side. The handle ID is its React Flow side. */
export const POSITION_BY_SIDE: Record<DiagramSide, Position> = {
  [DiagramSide.Top]: Position.Top,
  [DiagramSide.Right]: Position.Right,
  [DiagramSide.Bottom]: Position.Bottom,
  [DiagramSide.Left]: Position.Left,
};

const SIDE_BY_POSITION: Record<Position, DiagramSide> = {
  [Position.Top]: DiagramSide.Top,
  [Position.Right]: DiagramSide.Right,
  [Position.Bottom]: DiagramSide.Bottom,
  [Position.Left]: DiagramSide.Left,
};

const OPPOSITE_SIDE: Record<DiagramSide, DiagramSide> = {
  [DiagramSide.Top]: DiagramSide.Bottom,
  [DiagramSide.Right]: DiagramSide.Left,
  [DiagramSide.Bottom]: DiagramSide.Top,
  [DiagramSide.Left]: DiagramSide.Right,
};

/**
 * Returns the node side of a connection handle ID, or null when the ID is not
 * a side. The handle IDs of the builder tiles are React Flow sides.
 */
export function getHandleSide(
  handleID: string | null | undefined
): DiagramSide | null {
  return SIDE_BY_POSITION[handleID as Position] ?? null;
}

/**
 * Returns the nodes and sides of a React Flow connection, or null when a
 * handle ID is not a side.
 */
export function getConnectionEnds(
  connection: Connection
): DiagramConnectionEnds | null {
  const sourceSide = getHandleSide(connection.sourceHandle);
  const targetSide = getHandleSide(connection.targetHandle);
  if (!sourceSide || !targetSide) return null;
  return {
    sourceNodeID: connection.source,
    targetNodeID: connection.target,
    sourceSide,
    targetSide,
  };
}

/**
 * Returns the screen position of a mouse or touch event. A touch event uses
 * the touch that ended. Returns null when the event has no touch.
 */
export function getPointerClientPosition(
  event: MouseEvent | TouchEvent
): XYPosition | null {
  const pointer = 'changedTouches' in event ? event.changedTouches[0] : event;
  if (!pointer) return null;
  return { x: pointer.clientX, y: pointer.clientY };
}

/**
 * The size of a new node before the browser measures it, in canvas pixels.
 * An icon has no set width, so its width is an estimate for its default
 * label.
 */
const NEW_NODE_SIZE: Record<
  DiagramQuickAddTool,
  { width: number; height: number }
> = {
  [DiagramEditorTool.Tile]: { width: TILE_DEFAULT_WIDTH, height: 56 },
  [DiagramEditorTool.Icon]: { width: 96, height: 80 },
};

/**
 * Returns where to put a new node that a line from `sourceSide` connects to,
 * when the user drops the line at `drop`. The new node faces the line: its
 * side opposite `sourceSide` gets the line, and the middle of that side is at
 * `drop`. Returns the top-left corner of the node and the side that gets the
 * line.
 */
export function getConnectedNodePosition(
  sourceSide: DiagramSide,
  drop: XYPosition,
  tool: DiagramQuickAddTool
): { position: XYPosition; side: DiagramSide } {
  const side = OPPOSITE_SIDE[sourceSide];
  const { width, height } = NEW_NODE_SIZE[tool];
  const position: Record<DiagramSide, XYPosition> = {
    [DiagramSide.Top]: { x: drop.x - width / 2, y: drop.y },
    [DiagramSide.Right]: { x: drop.x - width, y: drop.y - height / 2 },
    [DiagramSide.Bottom]: { x: drop.x - width / 2, y: drop.y - height },
    [DiagramSide.Left]: { x: drop.x, y: drop.y - height / 2 },
  };
  return { position: position[side], side };
}
